import { stripTrailingSlashes } from "@pathwright/utils/src/url/path"
import CoverPage from "@pathwright/features/src/modules/cover-pages/CoverPage"
import {
  COVER_PAGE_CLOSE_TARGET_STATE_KEY,
  DEFAULT_COVER_PAGE_CLOSE_TARGET,
  getActiveCoverPageCloseTarget,
  normalizeCloseTargetCandidate
} from "@pathwright/features/src/modules/cover-pages/coverPageCloseTarget"
import { immersiveRoutes } from "@pathwright/features/src/modules/cover-pages/immersive/immersiveRouteConfig"
import { ImmersiveRoute } from "@pathwright/features/src/modules/cover-pages/types"
import { getResourceSlugId } from "@pathwright/features/src/modules/utils/urls"
import { getUrlFromRoute } from "@pathwright/ui/src/components/link/utils"
import { getDefaultLaunchedFromLibrary } from "lib/core/routing/utils"
import { useCallback } from "react"
import { useRouteMatch } from "react-router-dom"
import StoreOrLibraryBrowse from "../../store/StoreOrLibraryBrowse"
import OfferRegisterPanelComponent from "./OfferRegisterPanelComponent"

/** Navigate away from the cover page modal (preserves history so Back returns to cover). */
export function closeCoverPage(nextState: any) {
  const state = nextState?.location?.state
  const closeTargetFromState =
    typeof state?.[COVER_PAGE_CLOSE_TARGET_STATE_KEY] === "string"
      ? state[COVER_PAGE_CLOSE_TARGET_STATE_KEY]
      : null

  window.App?.navigate?.(
    getActiveCoverPageCloseTarget() ||
      closeTargetFromState ||
      getUrlFromRoute(nextState?.modalLaunchedFrom) ||
      getDefaultLaunchedFromLibrary(nextState) ||
      DEFAULT_COVER_PAGE_CLOSE_TARGET
  )
}

// @ts-expect-error: since this component is used in /client/features which doesn't import from
// /client/main, we need to window.global this component.
window.OfferRegisterPanelComponent = OfferRegisterPanelComponent

// Need to track which immersive route is active which could be lost due to order of route render
// because parent route (cover page) renders before child route (immersive) and anything that may
// trigger a re-render would result in losing the active immersive route state (passed to CoverPageProvider
// as immersiveRoute prop). So, we need to match the current path (current location.pathname) to the immersive
// route, and not based on the cover page route.

// Wrapper component that renders CoverPage and conditionally overlays immersive content
const CoverPageWrapper = (props: any) => {
  const {
    basePath,
    match: { params }
  } = props
  const { resourceSlug } = params

  const resourceId = getResourceSlugId(resourceSlug)
  const licensorSchoolIdRaw = params.licensorSchoolId ?? params.schoolId
  const licensorSchoolId = licensorSchoolIdRaw
    ? parseInt(String(licensorSchoolIdRaw), 10)
    : null

  // If cover page route match is exact, then we are not in an immersive route.
  const basePathRouteMatch = useRouteMatch({
    path: basePath,
    exact: false
  })
  const activeImmersiveRoute = basePathRouteMatch?.isExact
    ? null
    : props.immersiveRoute

  const baseCoverRoute = basePathRouteMatch?.url || ""

  const getImmersiveRoutePath = useCallback(
    (route: ImmersiveRoute) => {
      const immersiveRouteConfig = immersiveRoutes.find(
        (r) => r.key === route.name
      )
      // match.url may include a trailing slash; avoid cover//segment when joining
      const baseCover = stripTrailingSlashes(baseCoverRoute || "")
      const path = immersiveRouteConfig?.getPath
        ? immersiveRouteConfig.getPath({ baseCover, route })
        : null
      return path
    },
    [baseCoverRoute]
  )

  const modalLaunchedFrom = props.modalLaunchedFrom
    ? getUrlFromRoute(props.modalLaunchedFrom)
    : null

  const closeTarget = normalizeCloseTargetCandidate(
    (typeof props.location?.state?.[COVER_PAGE_CLOSE_TARGET_STATE_KEY] ===
    "string"
      ? props.location.state[COVER_PAGE_CLOSE_TARGET_STATE_KEY]
      : null) ||
      modalLaunchedFrom ||
      props.getDefaultLaunchedFrom?.(props) ||
      "/library/",
    baseCoverRoute
  )

  const handleClose = useCallback(
    () => closeCoverPage(props),
    [
      props.location,
      props.modalLaunchedFrom,
      props.match,
      props.getDefaultLaunchedFrom
    ]
  )

  return resourceId ? (
    <CoverPage
      onClose={handleClose}
      baseCoverRoute={baseCoverRoute}
      resourceId={resourceId}
      licensorSchoolId={licensorSchoolId}
      closeTarget={closeTarget || undefined}
      immersiveRoute={
        activeImmersiveRoute ? { ...params, ...activeImmersiveRoute } : null
      }
      getImmersiveRoutePath={getImmersiveRoutePath}
    />
  ) : null
}

const toArray = (v: any) => (Array.isArray(v) ? v : [v])

export function getCoverPageRoutes(basePath: string) {
  // Main cover page route structure

  const CoverPageWrapperWithBasePath = (props: any) => (
    <CoverPageWrapper {...props} basePath={basePath} />
  )

  return {
    path: basePath,
    components: {
      primary: StoreOrLibraryBrowse,
      chakraModal: CoverPageWrapperWithBasePath
    },
    isFullscreen: true,
    getDefaultLaunchedFrom: getDefaultLaunchedFromLibrary,
    childRoutes: immersiveRoutes.reduce((acc, route) => {
      const patterns = toArray(route.pathRe)
      patterns.forEach((path) => {
        acc.push({
          path,
          immersivePath: `/library/:resourceSlug/:cohortId(\\d+)?/cover/${path}`,
          // The name is used to identify the route being opened.
          immersiveRoute: { ...route, name: route.key },
          components: {
            chakraModal: CoverPageWrapperWithBasePath
          },
          onClose: closeCoverPage,
          isFullscreen: true
        })
      })
      return acc
    }, [] as any[]),
    // Chakra modal shell calls this with full route state (see Routes.js).
    onClose: closeCoverPage
  }
}

const coverPageRoutes = getCoverPageRoutes(
  "/:resourceSlug/:cohortId(\\d+)?/cover/"
)

// Support deprecated /library/* cover page routes
export const libraryCoverPageRoutes = getCoverPageRoutes(
  "/library/:resourceSlug/:cohortId(\\d+)?/cover/"
)

export default coverPageRoutes
